import {
  CapturePropertyRobotDTO,
  CapturePropertySaveDTO,
  ImagingScheduleEntryCaptureProfile,
  ImagingSchedulesDTO,
  ImagingSchedulesDeletedDataDTO,
  ImagingSchedulesEntryDTO,
  Robot,
  SaveCaptureProfilesDTO,
  SaveProfileDTO,
  SaveProfileTuningValuesDTO,
  SaveProfileVersionDTO,
} from 'business/models/Models';
import { FieldTypes, LightPathIds, externalConfig } from './constants';
import { RowExtraInfo, RowInfoType } from 'components/customComponents/Controls/RMTable';
import { DateFilterOptions, Field, ImageSettingsRowInfo, ScheduleRowInfo } from './types';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import {
  actionButtonStyle,
  actionButtonsDisable,
  actionButtonsEnable,
  iconSize,
} from '../CrystalContainer/Constants';
import { isEqual } from 'lodash';
import { FmlxColumn } from 'business/models/CustomModels';
import { FmlxTable } from 'fmlx-common-ui';
import DateHelper from 'utils/DateHelper';

export const ImageScheduleListCols: FmlxColumn[] = [
  {
    Header: 'Name',
    accessor: 'name',
    className: 'scheduleName alignLeft',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: true,
    headerStyle: {},
  },
  {
    Header: 'Enable',
    accessor: 'enable',
    className: 'alignLeft scheduleEnable gap-1',
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: true,
    headerStyle: {},
    filterDefinition: {
      type: FmlxTable.FilterType.TEXT,
      options: '',
      datepickerVariant: 'time',
      minDate: '',
      maxDate: '',
      component: '',
    },
  },
];

type DateCellArgs = {
  value?: Date | string | null;
  cell?: { value?: Date | string | null };
};

export const PlatesToImageCols: FmlxColumn[] = [
  {
    Header: 'Date To Image',
    accessor: 'dateToImage',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: true,
    headerStyle: {},
    Cell: (args: DateCellArgs) =>
      DateHelper.formatDateTimeOrEmpty(args.value ?? args.cell?.value),
  },
  {
    Header: 'User',
    accessor: 'user',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: true,
    headerStyle: {},
  },
  {
    Header: 'Experiment Name',
    accessor: 'experimentName',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: true,
    headerStyle: {},
  },
  {
    Header: 'Plate',
    accessor: 'plateNumber',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: true,
    headerStyle: {},
  },
];

export const PlateInspectionsToScoreCols: FmlxColumn[] = [
  {
    Header: 'Date Imaged',
    accessor: 'dateImaged',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    Cell: (args: DateCellArgs) =>
      DateHelper.formatDateTimeOrEmpty(args.value ?? args.cell?.value),
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: false,
    headerStyle: undefined,
  },
  {
    Header: 'User',
    accessor: 'user',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: false,
    headerStyle: undefined,
  },
  {
    Header: 'Experiment Name',
    accessor: 'experimentName',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: false,
    headerStyle: undefined,
  },
  {
    Header: 'Plate',
    accessor: 'plateNumber',
    className: 'alignLeft cursor-pointer',
    filterDefinition: { type: FmlxTable.FilterType.TEXT },
    editable: false,
    disableSort: false,
    hideSort: false,
    hideFilter: false,
    headerStyle: undefined,
  },
];

export const getRandomNegativeNumber = () => {
  return Math.floor(Math.random() * 1000) - 1000;
};

export const addEntryCaptureProfile = (entry: ImagingSchedulesEntryDTO, cpId: number) => {
  const tempEntryCp = new ImagingScheduleEntryCaptureProfile({
    id: getRandomNegativeNumber(),
    captureProfileId: cpId,
    imagingScheduleEntryId: entry?.id,
  });
  if (entry) {
    const isDuplicate = entry.imagingScheduleEntryCaptureProfile.some(
      (existingEntry) =>
        existingEntry.captureProfileId === tempEntryCp.captureProfileId &&
        existingEntry.imagingScheduleEntryId === tempEntryCp.imagingScheduleEntryId,
    );

    if (!isDuplicate) {
      entry.imagingScheduleEntryCaptureProfile = [
        ...entry.imagingScheduleEntryCaptureProfile,
        tempEntryCp,
      ];
    }
  }
};

export const removeEntryCaptureProfile = (
  entry: ImagingSchedulesEntryDTO,
  cpId: number,
  setDeletedData: React.Dispatch<React.SetStateAction<ImagingSchedulesDeletedDataDTO>>,
) => {
  const deletedIds = entry.imagingScheduleEntryCaptureProfile
    .filter((x) => x.captureProfileId === cpId)
    .map((x) => x.id);
  entry.imagingScheduleEntryCaptureProfile =
    entry?.imagingScheduleEntryCaptureProfile.filter((x) => x.captureProfileId !== cpId);
  setDeletedData((prevData) => {
    return new ImagingSchedulesDeletedDataDTO({
      ...prevData,
      imagingScheduleEntryCaptureProfileIds: Array.from(
        new Set([
          ...(prevData?.imagingScheduleEntryCaptureProfileIds || []),
          ...deletedIds,
        ]),
      ),
    });
  });
};

export const getEntryErrors = (entries: ImagingSchedulesEntryDTO[]): RowExtraInfo => {
  const entryErrors: RowExtraInfo = {};
  entries.forEach((entry, index) => {
    let msg = '';
    if (
      entries.find(
        (x) => x.days === entry.days && x.hours === entry.hours && x.id !== entry.id,
      )
    ) {
      msg += 'Combination of Days and Hours must be unique.\n';
    }
    if (entry.hours < 0 || entry.hours > 23) {
      msg += 'Hours must be between 0 and 23.\n';
    }
    if (msg) {
      entryErrors[index] = { msg: msg, type: RowInfoType.Error };
    }
  });
  return entryErrors;
};

export const validateSchedule = (
  schedule: ImagingSchedulesDTO,
  allSchedules: ImagingSchedulesDTO[],
  index: number,
): ScheduleRowInfo => {
  const entryErrors = getEntryErrors(schedule.imagingScheduleEntry || []);

  let msg = '';
  if (!schedule.name) {
    msg += 'Name cannot be empty.\n';
  } else if (allSchedules.find((x) => x.name === schedule.name && x.id !== schedule.id)) {
    msg += 'Name must be unique.\n';
  }

  if (Object.keys(entryErrors).length) {
    msg += 'There are errors in the schedule entry table.\n';
  }

  if (Object.keys(entryErrors).length || msg) {
    return {
      [index]: {
        msg: msg,
        type: RowInfoType.Error,
        entryErrors: entryErrors,
      },
    };
  }

  return {};
};

export const validateImageSettings = (
  imgSetting: SaveProfileDTO,
  imgSettingList: SaveProfileDTO[],
  index: number,
  robots: Robot[],
): ImageSettingsRowInfo => {
  let msg = '';
  if (!imgSetting.name) {
    msg += 'Name cannot be empty.\n';
  } else if (
    imgSettingList.find(
      (x) =>
        x.name.trim().toLowerCase() === imgSetting.name.trim().toLowerCase() &&
        x.id !== imgSetting.id,
    )
  ) {
    msg += 'Name must be unique.\n';
  }
  imgSetting?.capturePropertySaveDTO?.map((cpd) => {
    const robotName = robots?.find((x) => x.id === cpd.robotId)?.name;
    const robotJsonConfig = JSON.parse(
      robots?.find((r) => r.id === cpd.robotId)?.robotJson || '{}',
    );
    Object.keys(robotJsonConfig).map((k) => {
      if (k !== externalConfig) {
        const property = robotJsonConfig[k]?.find((x) => x.PropertyName === cpd.name);
        if (property?.NumericFieldProperty?.Step) {
          const step = Number(property.NumericFieldProperty.Step);
          const value = Number(cpd.value);
          if (!isMultipleOfStep(value, step)) {
            msg += `[${robotName}]: ${k}: ${property?.DisplayLabel} should be a multiple of ${step}\n`;
          }
        }
        if (
          roundValue(parseFloat(cpd.value)) <
            roundValue(property?.NumericFieldProperty?.Min) ||
          roundValue(parseFloat(cpd.value)) >
            roundValue(property?.NumericFieldProperty?.Max)
        ) {
          msg += `[${robotName}] ${k}: ${
            property.DisplayLabel
          } must be between ${roundValue(
            property.NumericFieldProperty?.Min,
          )} and ${roundValue(property.NumericFieldProperty?.Max)}\n`;
        }
      }
    });
  });
  imgSetting?.tuningValuesDTOs?.map((tuningValues) => {
    if (tuningValues.lowerLevelLimit >= tuningValues.upperLevelLimit) {
      msg += `Upper level limit must be greater than lower level limit of ${imgSetting.name} tuning values`;
    }
  });
  if (msg) {
    return {
      [index]: {
        msg: msg,
        type: RowInfoType.Error,
      },
    };
  }
  return {};
};

export const validateRobotSettings = (
  captureProperties: CapturePropertySaveDTO[],
  robotName: string,
  index: number,
  robotJsonConfig: string,
): ImageSettingsRowInfo => {
  let msg = '';
  captureProperties?.forEach((cp) => {
    let foundProperty = null;
    let lightPath;
    Object.keys(robotJsonConfig).some((key) => {
      foundProperty = robotJsonConfig[key]?.find((item) =>
        Object.values(item).includes(cp.name),
      );
      lightPath = key;
      return foundProperty !== undefined;
    });
    if (
      Number(cp.value) < Number(foundProperty?.NumericFieldProperty?.Min) ||
      Number(cp.value) > Number(foundProperty?.NumericFieldProperty?.Max)
    ) {
      msg += `${robotName}: ${lightPath}: ${foundProperty?.DisplayLabel} should be between ${foundProperty?.NumericFieldProperty?.Min} and ${foundProperty?.NumericFieldProperty?.Max}\n`;
    }
  });
  if (msg) {
    return {
      [index]: {
        msg: msg,
        type: RowInfoType.Error,
      },
    };
  }
  return {};
};

export const checkIsCurrentModified = (
  fetchedCp: SaveProfileDTO,
  currentCp: SaveProfileDTO,
) => {
  if (
    !isEqual(
      fetchedCp.capturePropertyRobotDTO.data,
      currentCp.capturePropertyRobotDTO.data,
    )
  ) {
    return true;
  }
  if (fetchedCp.importFl === currentCp.importFl) {
    if (fetchedCp.tuningValuesDTOs.length < 1 || currentCp.tuningValuesDTOs.length < 1) {
      return false;
    }
    const fTuningValues: SaveProfileTuningValuesDTO = fetchedCp.tuningValuesDTOs[0];
    const cTuningValues: SaveProfileTuningValuesDTO = currentCp.tuningValuesDTOs[0];
    if (
      fTuningValues.autoLevelingOn === cTuningValues.autoLevelingOn &&
      fTuningValues.upperLevelLimit === cTuningValues.upperLevelLimit &&
      fTuningValues.lowerLevelLimit === cTuningValues.lowerLevelLimit
    ) {
      return false;
    }
    return true;
  }
  return true;
};

const specialFalseFields = [
  'FRAP.FrapApplyRandomBleachForCenterDropArea',
  'FRAP.FrapApplyRandomBleachForNearEdgeDropArea',
];

export const prepareImageSettingsDTOs = (
  fetchedCpData,
  cpData,
  deletedImagingSettings,
) => {
  const saveCaptureProfilesDTO = new SaveCaptureProfilesDTO();
  saveCaptureProfilesDTO.captureProfilesDto = Object.keys(cpData).map((key) => {
    const profileDto = new SaveProfileDTO(cpData[key]);
    profileDto.isModified =
      profileDto.id > 0
        ? checkIsCurrentModified(fetchedCpData[profileDto.id], profileDto)
        : false;
    if (profileDto?.capturePropertyRobotDTO?.data) {
      profileDto.capturePropertySaveDTO = Object.keys(
        profileDto.capturePropertyRobotDTO?.data,
      )?.flatMap((k) => {
        return Object.entries(profileDto.capturePropertyRobotDTO?.data[k])
          ?.map(([Name, Value]) => {
            if (specialFalseFields.some((s) => s.includes(Name))) {
              if (Value === false) {
                Value = 'False';
              }
            }
            if (Value !== false && Value !== 'NaN') {
              return new CapturePropertySaveDTO({
                name: Name,
                value: Value,
                robotId: Number(k),
              });
            }
            return null;
          })
          .filter((r) => r !== null);
      });
    } else {
      profileDto.capturePropertyRobotDTO = new CapturePropertyRobotDTO();
    }
    return profileDto;
  });
  saveCaptureProfilesDTO.deletedProfileIds = [...deletedImagingSettings];
  return saveCaptureProfilesDTO;
};

export const getHeaderDeleteButton = (
  onClick: () => void,
  enabled: boolean,
): RMCustomButtonProps => {
  const headerButton: RMCustomButtonProps = {
    onClick: onClick,
    isDisabled: !enabled,
    titleTip: 'Delete',
    iconProps: {
      name: 'Delete',
      fill: 'white',
      ...iconSize,
    },
    contentContainerStyle: {
      ...actionButtonStyle,
      backgroundColor: enabled ? actionButtonsEnable.delete : actionButtonsDisable.delete,
    },
  };
  return headerButton;
};

export const DateFilters: DateFilterOptions[] = [
  {
    title: 'All',
    value: 'all',
  },
  {
    title: 'Last 7 days',
    value: '7',
  },
  {
    title: 'Last 14 days',
    value: '14',
  },
  {
    title: 'Last 30 days',
    value: '30',
  },
];

export const getScheduleDeleteMessage = (selected: number): string => {
  let bodyText = `Selected Imaging Schedule is in use and cannot be deleted.`;
  if (selected > 1) {
    bodyText = 'Some of the selected Imaging Schedules are in use and cannot be deleted.';
  }
  return bodyText;
};

export const getScheduleDefaultMessage = (): string => {
  return 'You cannot change enable status of default imaging schedule. Change the default imaging schedule and try again.';
};

export const createDefaultSaveProfileDTO = (robotId: number): SaveProfileDTO => {
  const tuningValues = new SaveProfileTuningValuesDTO();
  tuningValues.autoLevelingOn = false;
  tuningValues.autoLevelingMode = 0;
  tuningValues.autoLevelingLowThreshold = 0;
  tuningValues.autoLevelingHighThreshold = 0.2;
  tuningValues.upperLevelLimit = 255;
  tuningValues.lowerLevelLimit = 0;
  tuningValues.id = getRandomNegativeNumber();
  tuningValues.brightness = 0;
  tuningValues.contrast = 0;
  tuningValues.gamma = 0;

  const profileVersion = new SaveProfileVersionDTO();
  profileVersion.id = getRandomNegativeNumber();
  profileVersion.dateCreated = new Date();

  const newDto = new SaveProfileDTO();
  newDto.id = getRandomNegativeNumber();
  newDto.name = '';
  newDto.enabled = true;
  newDto.importFl = true;
  newDto.status = 1;
  newDto.experimentPlateId = null;
  newDto.parentID = null;
  newDto.isModified = false;
  newDto.hidden = false;
  newDto.inUse = false;
  newDto.tuningValuesDTOs = [tuningValues];
  newDto.profileVersionDTOs = [profileVersion];
  if (robotId) {
    const capturePropertyRobotDTO = new CapturePropertyRobotDTO();
    capturePropertyRobotDTO.data = {
      [robotId]: { LightPath: LightPathIds.Visible.toString() },
    };
    newDto.capturePropertyRobotDTO = capturePropertyRobotDTO;
  }
  return newDto;
};

export const getDropIntensityField = (prefix: string): Field => ({
  DisplayLabel: 'Drop Intensity Threshold Values:',
  UnitLabel: '',
  PropertyName: `${prefix}FrapDropIntensityThresholdValues`,
  IsMandatory: false,
  FieldType: FieldTypes.Boolean,
  EnableRules: [],
  VisibleRules: [],
  NumericFieldProperty: null,
  SingleOptionFieldProperty: null,
});

export const roundValue = (value: number) => {
  return parseFloat(value?.toFixed(3));
};

export function getThresholdValuesFields(prefix: string): Field[] {
  return [
    {
      DisplayLabel: 'Underexposed Threshold',
      UnitLabel: '',
      PropertyName: `${prefix}FrapDropIntensityUnderexposedThreshold`,
      IsMandatory: false,
      FieldType: FieldTypes.Range,
      EnableRules: [
        {
          PropertyName: `${prefix}FrapDropIntensityThresholdValues`,
          Value: 'True',
          Reversed: false,
        },
      ],
      VisibleRules: [],
      NumericFieldProperty: {
        Min: 0,
        Max: 255,
        Step: 1,
      },
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Overexposed Threshold',
      UnitLabel: '',
      PropertyName: `${prefix}FrapDropIntensityOverexposedThreshold`,
      IsMandatory: false,
      FieldType: FieldTypes.Range,
      EnableRules: [
        {
          PropertyName: `${prefix}FrapDropIntensityThresholdValues`,
          Value: 'True',
          Reversed: false,
        },
      ],
      VisibleRules: [],
      NumericFieldProperty: {
        Min: 0,
        Max: 255,
        Step: 1,
      },
      SingleOptionFieldProperty: null,
    },
  ];
}

export function getDropAreaField(prefix: string): Field {
  return {
    DisplayLabel: 'Drop area to include in Random Bleach Position',
    UnitLabel: '',
    PropertyName: `${prefix}FrapSetDropAreaToIncludeForRandomBleachPosition`,
    IsMandatory: false,
    FieldType: FieldTypes.Boolean,
    EnableRules: [],
    VisibleRules: [],
    NumericFieldProperty: null,
    SingleOptionFieldProperty: null,
  };
}

export function getDropAreaFields(prefix: string): Field[] {
  return [
    {
      DisplayLabel: '',
      UnitLabel: '',
      PropertyName: `${prefix}FrapApplyRandomBleachForCenterDropArea`,
      IsMandatory: false,
      FieldType: FieldTypes.Boolean,
      EnableRules: [],
      VisibleRules: [
        {
          PropertyName: `${prefix}FrapSetDropAreaToIncludeForRandomBleachPosition`,
          Value: 'True',
          Reversed: false,
        },
      ],
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Center',
      UnitLabel: '%',
      PropertyName: `${prefix}FrapDropAreaForCenterRandomBleachPositionPercentage`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [
        {
          PropertyName: `${prefix}FrapApplyRandomBleachForCenterDropArea`,
          Value: 'True',
          Reversed: false,
        },
      ],
      VisibleRules: [],
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: '',
      UnitLabel: '',
      PropertyName: `${prefix}FrapApplyRandomBleachForNearEdgeDropArea`,
      IsMandatory: false,
      FieldType: FieldTypes.Boolean,
      EnableRules: [],
      VisibleRules: [
        {
          PropertyName: `${prefix}FrapSetDropAreaToIncludeForRandomBleachPosition`,
          Value: 'True',
          Reversed: false,
        },
      ],
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Edge',
      UnitLabel: '%',
      PropertyName: `${prefix}FrapDropAreaForNearEdgeRandomBleachPositionPercentage`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [
        {
          PropertyName: `${prefix}FrapApplyRandomBleachForNearEdgeDropArea`,
          Value: 'True',
          Reversed: false,
        },
      ],
      VisibleRules: [],
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
  ];
}

export function getAcquireField(prefix: string): Field {
  return {
    DisplayLabel: 'Acquire images at fastest rate for',
    UnitLabel: 's',
    PropertyName: `${prefix}FrapNumberOfSecondsToTakeImagesAfterLaserShot`,
    IsMandatory: false,
    FieldType: FieldTypes.Numeric,
    EnableRules: [],
    VisibleRules: [],
    NumericFieldProperty: null,
    SingleOptionFieldProperty: null,
  };
}

export function getTimeWaitEndStateField(prefix: string): Field {
  return {
    DisplayLabel: 'Time to wait before taking end-state images:',
    UnitLabel: 's',
    PropertyName: `${prefix}FrapTimeToWaitBeforeTakingEndStateImages`,
    IsMandatory: false,
    FieldType: FieldTypes.Numeric,
    EnableRules: [],
    VisibleRules: [],
    NumericFieldProperty: null,
    SingleOptionFieldProperty: null,
  };
}

export function getImagingOptionsFields(prefix: string): Field[] {
  return [
    {
      DisplayLabel: 'Fluoro Light',
      UnitLabel: '%',
      PropertyName: `${prefix}FrapImagingTopLight`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Gamma',
      UnitLabel: '',
      PropertyName: `${prefix}FrapImagingGamma`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Exposure',
      UnitLabel: 'ms',
      PropertyName: `${prefix}FrapImagingExposure`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Gain',
      UnitLabel: 'dB',
      PropertyName: `${prefix}FrapImagingGain`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
  ];
}

export function getFocusingOptionsField(prefix: string): Field {
  return {
    DisplayLabel: 'Auto focus - Condenser',
    UnitLabel: '',
    PropertyName: `${prefix}FrapAutoFocusCondenser`,
    IsMandatory: false,
    FieldType: FieldTypes.Numeric,
    EnableRules: [],
    VisibleRules: null,
    NumericFieldProperty: null,
    SingleOptionFieldProperty: null,
  };
}

export function getBrightFieldFocusingFields(prefix: string): Field[] {
  return [
    {
      DisplayLabel: 'Bright Field',
      UnitLabel: '%',
      PropertyName: `${prefix}FrapBrightFieldFocusingBrightField`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Gamma',
      UnitLabel: '',
      PropertyName: `${prefix}FrapBrightFieldFocusingGamma`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Exposure',
      UnitLabel: 'ms',
      PropertyName: `${prefix}FrapBrightFieldFocusingExposure`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
    {
      DisplayLabel: 'Gain',
      UnitLabel: 'dB',
      PropertyName: `${prefix}FrapBrightFieldFocusingGain`,
      IsMandatory: false,
      FieldType: FieldTypes.Numeric,
      EnableRules: [],
      VisibleRules: null,
      NumericFieldProperty: null,
      SingleOptionFieldProperty: null,
    },
  ];
}

export function getGeneralCurveField(prefix: string): Field {
  return {
    DisplayLabel: 'Curve Fitting Options (Full Reovery Only)',
    UnitLabel: '',
    PropertyName: `${prefix}FrapCurveFittingComponentModel`,
    IsMandatory: false,
    FieldType: 2,
    EnableRules: [],
    VisibleRules: null,
    NumericFieldProperty: null,
    SingleOptionFieldProperty: {
      Options: [
        {
          Label: 'Single Component Fitting',
          Value: '1',
        },
        {
          Label: 'Double Component Fitting',
          Value: '2',
        },
      ],
    },
  };
}

export const isMultipleOfStep = (value: number, step: number): boolean => {
  if (step === 0) {
    return true;
  }
  const precision = 1e-10;
  const ratio = value / step;
  const roundedRatio = Math.round(ratio);
  return Math.abs(ratio - roundedRatio) < precision;
};
